/* tslint:disable:a11y-role-has-required-aria-props */
import * as React from 'react';
import cx from 'classnames';
import { Table, Input } from 'reactstrap';
import { CompetitionPledge, CompetitionPledgeCategories, PledgeCompletions, CompetitionScoreLevel } from '../userApi';
import { LProps, withLocalization } from '../utils/wrappers';
import { Link } from 'react-router-dom';
import { makePledgePath, makePledgeCategoryPath, makeActionSavingsPath } from '../utils/routerHelper';
import { State } from '../redux/state';
import * as selectors from '../redux/selectors';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { getScoreLevelPhoto, CompetitionPledges, getCompetitionUserScore } from '../utils/userApiHelper';
import { estimateActionSavings } from '../utils/actionSavings';
import LinkButton from './LinkButton';
import PledgesTableHelp from './PledgesTableHelp';
import PledgesCatalogBreakdownLink from './PledgesCatalogBreakdownLink';

type SortableColumn = 'shortTitle' | 'category' | 'points' | 'savingsCO2Lbs' | 'dollarsSaved' | 'completions';

interface ManualProps {
  competitionPledgeCategoryID: number;
  changeComponent: React.ReactNode;

}

interface PledgesByScoreLevel {
  scoreLevel?: CompetitionScoreLevel;
  pledges: CompetitionPledge[];
  active: boolean;
}

interface Props extends ManualProps {
  pledgesByLevel: PledgesByScoreLevel[];
  categories: CompetitionPledgeCategories;
  completions: PledgeCompletions;
  userIsSomeUniversityAdmin: boolean;
  canBypassPledgeLocks: boolean;
  userPoints: number;
  accessiblePledgeIds: { [pledgeId: number]: boolean };
  featuredPledgeUUIDs: string[];
  featuredPledgeLabel?: string;
  completionTotals: { [pledgeId: number]: number };
}

interface PledgesTableState {
  helpIsOpen: boolean;
  sortColumn: SortableColumn | null;
  sortDirection: 'asc' | 'desc';
  nameFilter: string;
  categoryFilter: string; // 'All' or category ID
  completedFilter: 'All' | 'Completed' | 'Not Completed';
  pointsMinFilter: string;
  pointsMaxFilter: string;
}

const initialState: PledgesTableState = {
  helpIsOpen: false,
  sortColumn: null,
  sortDirection: 'desc',
  nameFilter: '',
  categoryFilter: 'All',
  completedFilter: 'All',
  pointsMinFilter: '',
  pointsMaxFilter: ''
};

class PledgesTable extends React.Component<LProps<Props>, PledgesTableState> {
  public state = initialState;

  private handleSort = (column: SortableColumn) => {
    this.setState((prevState) => {
      const isAsc = prevState.sortColumn === column && prevState.sortDirection === 'asc';
      return {
        sortColumn: column,
        sortDirection: isAsc ? 'desc' : 'asc'
      };
    });
  }

  private getSortIndicator = (column: SortableColumn) => {
    const { sortColumn, sortDirection } = this.state;
    if (sortColumn !== column) {
      return null;
    }
    return sortDirection === 'asc' ? ' ▲' : ' ▼';
  }
  private pointsNeededForLevel(scoreLevel: CompetitionScoreLevel): number {
    return Math.max(0, (scoreLevel.threshold || 0) - this.props.userPoints);
  }

  public render() {
    const {
      competitionPledgeCategoryID, pledgesByLevel, t, formatNumber, categories, completions,
      userIsSomeUniversityAdmin, canBypassPledgeLocks, changeComponent, accessiblePledgeIds,
      featuredPledgeUUIDs, featuredPledgeLabel, completionTotals
    } = this.props;
    const { sortColumn, sortDirection, nameFilter, categoryFilter, completedFilter, pointsMinFilter, pointsMaxFilter } = this.state;

    const minPoints = parseInt(pointsMinFilter, 10);
    const maxPoints = parseInt(pointsMaxFilter, 10);
    // Hide empty-name categories (system/onboarding bucket) from the filter.
    const categoryOptions = Object.keys(categories)
      .map((id) => categories[id])
      .filter((cat) => cat && String(cat.name || '').trim().length > 0);

    const renderPledgesByLevel = pledgesByLevel.map((level) => {
      let processPledges = level.pledges;

      // Filter
      processPledges = processPledges.filter((pledge) => {
        if (nameFilter && !pledge.shortTitle.toLowerCase().includes(nameFilter.toLowerCase())) return false;
        if (categoryFilter !== 'All' && pledge.competitionPledgeCategoryID && pledge.competitionPledgeCategoryID.toString() !== categoryFilter) return false;
        const isCompleted = (completions[pledge.id] || 0) >= pledge.repeats;
        if (completedFilter === 'Completed' && !isCompleted) return false;
        if (completedFilter === 'Not Completed' && isCompleted) return false;
        if (!isNaN(minPoints) && pledge.points < minPoints) return false;
        if (!isNaN(maxPoints) && pledge.points > maxPoints) return false;
        return true;
      });

      // Sort
      if (sortColumn) {
        processPledges = [...processPledges].sort((a, b) => {
          let valA: string | number;
          let valB: string | number;

          switch (sortColumn) {
            case 'shortTitle':
              valA = a.shortTitle.toLowerCase();
              valB = b.shortTitle.toLowerCase();
              break;
            case 'category':
              valA = ((categories[a.competitionPledgeCategoryID] && categories[a.competitionPledgeCategoryID].name) || '').toLowerCase();
              valB = ((categories[b.competitionPledgeCategoryID] && categories[b.competitionPledgeCategoryID].name) || '').toLowerCase();
              break;
            case 'points':
              valA = a.points;
              valB = b.points;
              break;
            case 'savingsCO2Lbs':
              valA = a.savingsCO2Lbs;
              valB = b.savingsCO2Lbs;
              break;
            case 'dollarsSaved':
              valA = estimateActionSavings(a).dollarsSaved || 0;
              valB = estimateActionSavings(b).dollarsSaved || 0;
              break;
            case 'completions':
              valA = completions[a.id] || 0;
              valB = completions[b.id] || 0;
              break;
            default:
              return 0;
          }

          const comparison = valA < valB ? -1 : (valA > valB ? 1 : 0);
          if (comparison !== 0) {
            return sortDirection === 'asc' ? comparison : -comparison;
          }
          return a.id - b.id; // stable sort
        });
      }
      return { ...level, pledges: processPledges };
    });

    const columnCount = competitionPledgeCategoryID === 0 ? 6 : 5;

    return (
      <div>
        <div className="cc__pledges_filters cc__white_box" style={{ marginBottom: '1rem' }}>
          <div className="cc__pledges_filters__group">
            <label>{t('Search by Name')}</label>
            <Input
              type="text"
              placeholder={t('Pledge Name')}
              value={nameFilter}
              onChange={(e) => this.setState({ nameFilter: e.target.value })}
            />
          </div>
          <div className="cc__pledges_filters__group">
            <label>{t('Category')}</label>
            <Input
              type="select"
              value={categoryFilter}
              onChange={(e) => this.setState({ categoryFilter: e.target.value })}
            >
              <option value="All">{t('All Categories')}</option>
              {categoryOptions.map((cat) => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </Input>
          </div>
          <div className="cc__pledges_filters__group">
            <label>{t('Status')}</label>
            <Input
              type="select"
              value={completedFilter}
              onChange={(e) => this.setState({ completedFilter: e.target.value as any })}
            >
              <option value="All">{t('All')}</option>
              <option value="Completed">{t('Completed')}</option>
              <option value="Not Completed">{t('Not Completed')}</option>
            </Input>
          </div>
          <div className="cc__pledges_filters__group cc__pledges_filters__points">
            <label>{t('Points Range')}</label>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <Input
                type="number"
                placeholder={t('Min')}
                value={pointsMinFilter}
                onChange={(e) => this.setState({ pointsMinFilter: e.target.value })}
                min={0}
              />
              <Input
                type="number"
                placeholder={t('Max')}
                value={pointsMaxFilter}
                onChange={(e) => this.setState({ pointsMaxFilter: e.target.value })}
                min={0}
              />
            </div>
          </div>
        </div>
        <div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
            {changeComponent}
          </div>
        </div>
        <LinkButton
          onClick={() => this.setState({ helpIsOpen: true })}
        >{t('How is my score calculated?')}</LinkButton>
        {' · '}
        <PledgesCatalogBreakdownLink />
        {' · '}
        <Link to={makeActionSavingsPath('methodology')}>{t('Savings methodology')}</Link>
        <Table id="pledges_table" size="sm" >
          <thead>
            <tr className="table-primary">
              <th className="cc__title__column" onClick={() => this.handleSort('shortTitle')} style={{ cursor: 'pointer' }}>
                {t('Title')}
                {this.getSortIndicator('shortTitle')}
              </th>
              {competitionPledgeCategoryID === 0 && (
                <th className="cc__category__column" onClick={() => this.handleSort('category')} style={{ cursor: 'pointer' }}>
                  {t('Category')}
                  {this.getSortIndicator('category')}
                </th>
              )}
              <th onClick={() => this.handleSort('points')} style={{ cursor: 'pointer' }}>
                {t('Points')}
                {this.getSortIndicator('points')}
              </th>
              <th onClick={() => this.handleSort('savingsCO2Lbs')} style={{ cursor: 'pointer' }}>
                <div className="cc__desktop__label">
                  {t('Pounds CO')}
                  <sub>2</sub>
                  {t('savings')}
                </div>
                <div className="cc__mobile__label">
                  {t('CO')}<sub>2</sub>
                </div>
                {this.getSortIndicator('savingsCO2Lbs')}
              </th>
              <th onClick={() => this.handleSort('dollarsSaved')} style={{ cursor: 'pointer' }}>
                <div className="cc__desktop__label">{t('SF $ saved')}</div>
                <div className="cc__mobile__label">{t('$')}</div>
                {this.getSortIndicator('dollarsSaved')}
              </th>
              <th onClick={() => this.handleSort('completions')} style={{ cursor: 'pointer' }}>
                {t('Completions')}
                {this.getSortIndicator('completions')}
              </th>
            </tr>
          </thead>
          {renderPledgesByLevel.map(({ pledges, scoreLevel, active }) => {
            const pointsNeeded = this.pointsNeededForLevel(scoreLevel);
            const isLockedLevel = !active && !canBypassPledgeLocks;
            const popularityBadges = selectors.pledgePopularityBadgesInSection(pledges, completionTotals);
            return (
            <tbody key={scoreLevel.id}>
              {(pledges.length > 0) &&
                <tr>
                  <th
                    className={cx('cc__pledges_table__score_level', { 'table-active': isLockedLevel })}
                    colSpan={columnCount}
                  >
                    {getScoreLevelPhoto(scoreLevel) ? (
                      <img src={getScoreLevelPhoto(scoreLevel)} alt={scoreLevel.name} />
                    ) : <span className="placeholder" />}
                    &nbsp;{scoreLevel.name}
                    {isLockedLevel && pointsNeeded > 0 &&
                      <span className="cc__pledges_level_points_hint">
                        {t('({{more}} more points to unlock, {{total}} total)', {
                          more: formatNumber(pointsNeeded),
                          total: formatNumber(scoreLevel.threshold || 0)
                        })}
                      </span>
                    }
                  </th>
                </tr>
              }
              {pledges.map((pledge) => {
                const dollarsSaved = estimateActionSavings(pledge).dollarsSaved;
                const scoreLocked = !active && !canBypassPledgeLocks;
                const gateLocked = !accessiblePledgeIds[pledge.id] && !scoreLocked && !canBypassPledgeLocks;
                const isLocked = scoreLocked || gateLocked;
                const canOpen = !isLocked;
                const isFeatured = featuredPledgeUUIDs.indexOf(pledge.uuid) >= 0;
                const isMostPopular = Boolean(popularityBadges.mostPopular[pledge.id]);
                const isPopular = Boolean(popularityBadges.popular[pledge.id]);
                return (
                <tr
                  key={pledge.id}
                  className={cx({
                    'table-success': !isLocked && completions[pledge.id] >= pledge.repeats,
                    'table-warning': !isLocked && completions[pledge.id] >= 1 && completions[pledge.id] < pledge.repeats,
                    'cc__pledge_inactive': isLocked
                  })}
                >
                  <td className="cc__title__column">
                    {(isFeatured || isMostPopular || isPopular) &&
                      <div className="cc__pledge_table__designations">
                        {isFeatured &&
                          <span className="cc__pledge_designation cc__pledge_designation--featured">
                            {featuredPledgeLabel || t('Featured action')}
                          </span>
                        }
                        {isMostPopular &&
                          <span className="cc__pledge_designation cc__pledge_designation--most_popular">
                            {t('Most Popular')}
                          </span>
                        }
                        {isPopular &&
                          <span className="cc__pledge_designation cc__pledge_designation--popular">
                            {t('Popular')}
                          </span>
                        }
                      </div>
                    }
                    {canOpen && <Link to={makePledgePath(pledge, t)}>{pledge.shortTitle}</Link>}
                    {!canOpen && pledge.shortTitle}
                  </td>
                  {competitionPledgeCategoryID === 0 &&
                    <td className="cc__category__column">
                      {canOpen ? (
                        <Link
                          to={makePledgeCategoryPath(pledge.competitionPledgeCategoryID, t)}
                        >{categories[pledge.competitionPledgeCategoryID] ? categories[pledge.competitionPledgeCategoryID].name : ''}</Link>
                      ) : (
                        categories[pledge.competitionPledgeCategoryID] ? categories[pledge.competitionPledgeCategoryID].name : ''
                      )}
                    </td>
                  }
                  <td>{pledge.points}</td>
                  <td>{formatNumber(pledge.savingsCO2Lbs, 0)}</td>
                  <td>{dollarsSaved ? `$${formatNumber(dollarsSaved, 2)}` : '—'}</td>
                  <td style={{ textAlign: 'center' }}>
                    {completions[pledge.id] === pledge.repeats && <i className={'fa fa-check'} />}
                    {completions[pledge.id] !== pledge.repeats && <span>{completions[pledge.id] || 0} / {pledge.repeats}</span>}
                  </td>
                </tr>
                );
              })}
            </tbody>
          ); })}
        </Table>
        <PledgesTableHelp
          isOpen={this.state.helpIsOpen}
          toggle={() => this.setState({ helpIsOpen: !this.state.helpIsOpen })}
        />
      </div>
    );
  }
}

const mapStateToProps = (state: State, ownProps: ManualProps) => {
  const user = state.user;
  const competitionUserScore = user
    ? getCompetitionUserScore(state.competitionUserScores, user.id)
    : undefined;
  const accessiblePledgeIds: { [pledgeId: number]: boolean } = {};
  Object.keys(state.competitionPledges || {}).forEach((id) => {
    const pledge = state.competitionPledges[Number(id)];
    if (pledge) {
      accessiblePledgeIds[pledge.id] = selectors.isPledgeAccessible(state, pledge);
    }
  });
  return {
    pledgesByLevel: selectors.pledgesByCategoryAndLevel(state, ownProps),
    categories: state.competitionPledgeCategories,
    completions: state.userPledgeCompletions,
    userIsSomeUniversityAdmin: selectors.userIsSomeUniversityAdmin(state),
    canBypassPledgeLocks: selectors.canBypassPledgeParticipantRestrictions(state),
    userPoints: competitionUserScore ? competitionUserScore.points : 0,
    accessiblePledgeIds,
    featuredPledgeUUIDs: selectors.getFeaturedPledgeUUIDs(state),
    featuredPledgeLabel: state.competition && state.competition.config
      ? state.competition.config.featuredPledgeLabel
      : undefined,
    completionTotals: selectors.getCompetitionPledgeCompletionTotals(state)
  };
};

export default compose(
  withLocalization('pledges'),
  connect(mapStateToProps)
)(PledgesTable) as React.SFC<ManualProps>;
